"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { TestimonialCard } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData } from "@storyblok/react";

interface TestimonialItem {
  name: string;
  content: TestimonialCard & SbBlokData;
  id: number;
  uuid: string;
  [k: string]: unknown;
}

interface TestimonialProps {
  testimonials: TestimonialItem[];
  hideHeading?: boolean;
  heading?: string;
}

const TestimonialComponent: React.FC<TestimonialProps> = ({
  testimonials,
  hideHeading = false,
  heading = "What our customers say",
}) => {
  if (!testimonials || testimonials.length === 0) return null;

  const validTestimonials = testimonials.filter(item => item && item.content);
  const isSingleTestimonial = validTestimonials.length === 1;

  if (isSingleTestimonial) {
    const testimonial = validTestimonials[0].content;

    return (
      <div id="carouselTestimonial" className="carousel carousel-testimonial d-flex flex-column" data-bs-ride="carousel" data-bs-interval="10000">
        <div className="carousel-inner">
          <div className="carousel-item active">
            <div className="row justify-content-center">
              <div className="col-lg-18 col-xl-14">
                <blockquote className="unstyled text-center mb-3 mb-md-4 pb-md-2">
                  {testimonial.copy}
                </blockquote>
              </div>
            </div>
            <div className="d-flex flex-column flex-md-row justify-content-center align-items-center">
              {testimonial.logo?.filename && testimonial.url?.cached_url && (
                <Link href={testimonial.url.cached_url} target="_blank" rel="noopener">
                  <Image
                    src={testimonial.logo.filename}
                    alt={testimonial.logo.alt || `${testimonial.company} logo`}
                    width={96}
                    height={25}
                    className="testimonial-logo"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    loading="lazy"
                    quality={75}
                  />
                </Link>
              )}
              <div className="text-center mt-3 d-md-none">
                <div className="fw-bold text-dark mb-0">{testimonial.name}</div>
                {testimonial.position && <div className="text-muted">{testimonial.position}</div>}
                {testimonial.company && (
                  <div className="text-muted">{testimonial.company}</div>
                )}
              </div>
              <div className="ms-md-4 d-none d-md-flex align-items-center">
                <div>
                  <div className="fw-bold text-dark mb-0">{testimonial.name}</div>
                  <div className="text-muted">{testimonial.company} {testimonial.position}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="padding-small">
        {!hideHeading && (
          <h2 className="text-center">{heading}</h2>
        )}
        <div className="two-thirds-centered">
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={30}
            slidesPerView={1}
            navigation={false}
            pagination={{
              clickable: true,
              renderBullet: (index, className) => {
                return `<span class="${className} custom-dot"></span>`;
              },
            }}
            autoplay={{ delay: 5000 }}
            loop
            className="owl-carousel owl-theme testimonial-slider"
          >
            {validTestimonials.map((item) => (
              <SwiperSlide key={item.content._uid}>
                <div className="testimonial">
                  <div className="testimonial-image">
                    {item.content.logo?.filename && (
                      item.content.url?.cached_url ? (
                        <Link href={item.content.url.cached_url} target="_blank" rel="noopener">
                          <Image
                            src={item.content.logo.filename}
                            alt={item.content.logo.alt || `${item.content.company} Logo`}
                            title={item.content.company}
                            width={180}
                            height={48}
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                            loading="lazy"
                            quality={75}
                          />
                        </Link>
                      ) : (
                        <Image
                          src={item.content.logo.filename}
                          alt={item.content.logo.alt || `${item.content.company} Logo`}
                          title={item.content.company}
                          width={180}
                          height={48}
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                          loading="lazy"
                          quality={75}
                        />
                      )
                    )}
                  </div>
                  <div className="testimonial-content">
                    <p>{item.content.copy}</p>
                    <hr />
                    <div className="testimonial-summary d-flex align-items-center justify-content-between">
                      <div>
                        <p>{item.content.name}, {item.content.position}</p>
                        <p>{item.content.company}</p>
                      </div>
                      {item.content.cta_button && item.content.cta_button_link?.cached_url && (
                        <Link
                          href={item.content.cta_button_link.cached_url}
                          className="button button-medium button-primary-outline"
                          target="_blank"
                        >
                          {item.content.cta_button}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>
  );
};

export default TestimonialComponent;